import { demTileCache } from './dem-tile-cache.js'
import { DEM_TILE_SIZE, DEM_MAX_ZOOM } from './dem-config.js'

const QUERY_ZOOM = 12
const TILE_SIZE = DEM_TILE_SIZE

function lon2tile(lon: number, zoom: number): number {
  return ((lon + 180) / 360) * Math.pow(2, zoom)
}

function lat2tile(lat: number, zoom: number): number {
  const latRad = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * Math.pow(2, zoom)
}

function decodeTerrariumElevation(r: number, g: number, b: number): number {
  return r * 256 + g + b / 256 - 32768
}

class ElevationPointService {
  async getElevation(lat: number, lng: number): Promise<number | null> {
    const tileX = Math.floor(lon2tile(lng, QUERY_ZOOM))
    const tileY = Math.floor(lat2tile(lat, QUERY_ZOOM))

    const pixels = await demTileCache.getTile(QUERY_ZOOM, tileX, tileY)
    if (!pixels) return null

    const fracX = lon2tile(lng, QUERY_ZOOM) - tileX
    const fracY = lat2tile(lat, QUERY_ZOOM) - tileY
    const px = Math.floor(fracX * TILE_SIZE)
    const py = Math.floor(fracY * TILE_SIZE)
    const idx = (py * TILE_SIZE + px) * 4
    return decodeTerrariumElevation(pixels[idx], pixels[idx + 1], pixels[idx + 2])
  }

  /**
   * Compute slope aspect (compass direction the slope faces) at a point using DEM finite differences.
   * Returns degrees [0, 360) where 0=N, 90=E, 180=S, 270=W, or null if DEM unavailable.
   */
  async getAspect(lat: number, lng: number): Promise<number | null> {
    const tileX = Math.floor(lon2tile(lng, QUERY_ZOOM))
    const tileY = Math.floor(lat2tile(lat, QUERY_ZOOM))

    const pixels = await demTileCache.getTile(QUERY_ZOOM, tileX, tileY)
    if (!pixels) return null

    const fracX = lon2tile(lng, QUERY_ZOOM) - tileX
    const fracY = lat2tile(lat, QUERY_ZOOM) - tileY
    const px = Math.floor(fracX * TILE_SIZE)
    const py = Math.floor(fracY * TILE_SIZE)

    if (px < 1 || px >= TILE_SIZE - 1 || py < 1 || py >= TILE_SIZE - 1) return null

    const elev = (x: number, y: number): number => {
      const i = (y * TILE_SIZE + x) * 4
      return decodeTerrariumElevation(pixels[i], pixels[i + 1], pixels[i + 2])
    }

    const cellSize = metersPerPixel(lat, QUERY_ZOOM)
    const dzdx = (elev(px + 1, py) - elev(px - 1, py)) / (2 * cellSize)
    const dzdy = (elev(px, py + 1) - elev(px, py - 1)) / (2 * cellSize)

    const slopeMag = Math.sqrt(dzdx * dzdx + dzdy * dzdy)
    if (slopeMag < 0.01) return null // flat terrain has no meaningful aspect

    // atan2(-dzdy, -dzdx) gives downhill direction; convert from math angle to compass bearing
    const rad = Math.atan2(-dzdy, -dzdx)
    // math angle: 0=east, π/2=north → compass: 0=north, π/2=east
    return (90 - (rad * 180) / Math.PI + 360) % 360
  }

  /**
   * DEM slope as a vector: unit uphill direction in local east/north plus the
   * grade (rise/run). Used to bias (not snap) track corrections — e.g. whether an
   * excursion runs uphill. Defaults to z16 (~2 m/px) so it resolves couloir-scale
   * relief, not just the broad mountainside. Note tile-Y grows southward, so the
   * north component is `-dzdy`. Returns null on flat terrain / tile edge / no tile.
   */
  async getSlopeVector(
    lat: number,
    lng: number,
    zoom = DEM_MAX_ZOOM
  ): Promise<{ east: number; north: number; grade: number } | null> {
    const tileX = Math.floor(lon2tile(lng, zoom))
    const tileY = Math.floor(lat2tile(lat, zoom))

    const pixels = await demTileCache.getTile(zoom, tileX, tileY)
    if (!pixels) return null

    const px = Math.floor((lon2tile(lng, zoom) - tileX) * TILE_SIZE)
    const py = Math.floor((lat2tile(lat, zoom) - tileY) * TILE_SIZE)
    if (px < 1 || px >= TILE_SIZE - 1 || py < 1 || py >= TILE_SIZE - 1) return null

    const elev = (x: number, y: number): number => {
      const i = (y * TILE_SIZE + x) * 4
      return decodeTerrariumElevation(pixels[i], pixels[i + 1], pixels[i + 2])
    }

    const cellSize = metersPerPixel(lat, zoom)
    const dzdx = (elev(px + 1, py) - elev(px - 1, py)) / (2 * cellSize) // d(elev)/d(east)
    const dzdy = (elev(px, py + 1) - elev(px, py - 1)) / (2 * cellSize) // d(elev)/d(south)
    const grade = Math.sqrt(dzdx * dzdx + dzdy * dzdy)
    if (grade < 0.01) return null

    // uphill gradient in east/north: north = -dzdy (tile-Y grows southward)
    return { east: dzdx / grade, north: -dzdy / grade, grade }
  }
}

function metersPerPixel(lat: number, zoom: number): number {
  return ((156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)) * (256 / TILE_SIZE)
}

export const elevationPointService = new ElevationPointService()
